import { computed, type MaybeRefOrGetter } from "vue"
import type { RecordFieldsFragment as RecordRow } from "@auger/core/gql/types"
import { getAuger } from "@/init/auger"
import { getType } from "@/stores/types"
import { useRecord, type UseRecordDeps } from "./useRecord"
import { canEditTask, updateTaskStatus } from "./useTasks"

export interface CreateTaskInput {
  name: string
  description?: string | null
  dueDate?: string | null
  assigneeIds?: number[]
  relatedIds?: number[]
}

/**
 * Create a task plus its assignee/related links, in one transaction.
 *
 * This used to be three round trips — insert the record, then one bulk link
 * insert per field — with nothing tying them together. A failure on the second
 * left a task with half its links and no error the caller could act on. It also
 * had to fabricate `Field` objects by hand to tell relationService which rows to
 * write, duplicating what the schema already says.
 *
 * The domain reads the schema itself, so `assigned_to` and `related_to` are just
 * keys in the patch: it routes them to stamped record_links rows inside the same
 * nested insert as the parent, and a failing child rolls the whole thing back.
 *
 * `slug` is omitted: records.slug is filled by the set_default_record_slug
 * trigger via generate_unique_slug — collision-checked inside the INSERT with
 * transliteration (unaccent), where the old findFreeSlug round-tripped up to
 * 20 times, checked through the caller's own read permissions, and DROPPED
 * non-ASCII outright ("Café" → "caf" where the DB transliterates to "cafe"),
 * so the two generators minted different slugs for the same name.
 */
export async function createTask(
  input: CreateTaskInput,
  { client }: Pick<UseRecordDeps, "client">
): Promise<RecordRow> {
  // ?? undefined, never Number(...): a type row fetched without schema_id
  // yields NaN, and NaN != null — the domain then reports "record #0 names
  // schema NaN", a wiring-shaped error for a data gap. Omitted, the domain
  // resolves the type's default schema itself.
  const raw = getType("task")?.schema_id
  const schemaId = typeof raw === "number" ? raw : undefined

  const data: Record<string, unknown> = { status: "todo" }
  if (input.dueDate) data.due_date = input.dueDate
  if (input.assigneeIds?.length) data.assigned_to = input.assigneeIds
  if (input.relatedIds?.length) data.related_to = input.relatedIds

  const name = input.name.trim()
  const auger = getAuger(client)
  const created = await auger.records.create({
    type: "task",
    name,
    description: input.description?.trim() || null,
    schema_id: schemaId,
    data,
  })

  return created
}

/**
 * A single task on top of useRecord, layering the two task rules the generic
 * record form can't express:
 *  - a `status` write also stamps/clears data.completed_at on completion
 *    transitions (parity with the kanban + MyTasks surfaces).
 *  - non-assignees get a read-only view (canEditTask); admins and unassigned
 *    tasks stay editable.
 *
 * Everything else (name, description, due_date, and the assigned_to/related_to
 * link fields) flows through the standard useRecord path unchanged.
 */
export function useTask(
  options: { slug?: MaybeRefOrGetter<string | null | undefined> },
  deps: UseRecordDeps
) {
  const record = useRecord({ type: "task", slug: options.slug }, deps)

  const readOnly = computed(() => {
    const task = record.record.value
    return !task || !canEditTask(task)
  })

  const updateField = (fieldKey: string, value: unknown) => {
    if (fieldKey === "status") {
      const task = record.record.value
      if (!task) return Promise.resolve(false)
      return record.update(updateTaskStatus(task, value), { silent: true })
    }
    return record.updateField(fieldKey, value)
  }

  return { ...record, updateField, readOnly }
}
